"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    }
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var classNames = require("classnames");
var PropTypes = require("prop-types");
var React = require("react");
var react_1 = require("react");
var ReactDOM = require("react-dom");
var CloseIcon_1 = require("../Icons/CloseIcon");
var Components_1 = require("../../utils/Components");
var Provider_types_1 = require("../Provider/Provider.types");
var Modal_1 = require("./Modal");
var childValidator = function (child) {
    var hasOwnProperty = Object.prototype.hasOwnProperty;
    if (!hasOwnProperty.call(child, 'props')) {
        return true;
    }
    var element = child;
    return element.type !== Modal_1.default;
};
var propTypes = {
    open: PropTypes.bool.isRequired,
    fullviewport: PropTypes.bool,
    header: PropTypes.node,
    dismissible: PropTypes.bool,
    onDismiss: PropTypes.func,
    backdropDismissesModal: PropTypes.bool,
    dismissTimeout: PropTypes.number,
    useEscKeyToDismiss: PropTypes.bool,
    viewport: PropTypes.oneOf(['fixed', 'relative']),
    children: Components_1.childTreeValidator(childValidator, function (componentName) {
        return componentName + " cannot contain another " + componentName + " instance as child element.";
    })
};
/**
 * This component provides a dialog box that covers the page and requires
 * focus and special attention from the user.
 * At the same time the rest of the UI is shielded from changes by the user.
 *
 * For example it makes sense to use this component if a major technical
 * error has happened and user interaction is seriously hindered as a
 * consequence.
 */
var UnmanagedModalWithinContext = /** @class */ (function (_super) {
    __extends(UnmanagedModalWithinContext, _super);
    function UnmanagedModalWithinContext(props) {
        var _this = _super.call(this, props) || this;
        _this.createPortalContainer = function () {
            var theme = _this.props.ergoElements.theme;
            _this.portalContainer = document.createElement('div');
            _this.portalContainer.setAttribute('data-version', Provider_types_1.elementsVersion);
            _this.portalContainer.setAttribute('data-theme', theme);
        };
        _this.handleBackdrop = function (event) {
            if (_this.props.fullviewport) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            var _a = _this.props, dismissible = _a.dismissible, backdropDismissesModal = _a.backdropDismissesModal;
            if (dismissible && backdropDismissesModal) {
                _this.dismiss();
            }
        };
        _this.handleKeyDown = function (event) {
            var _a = _this.props, dismissible = _a.dismissible, useEscKeyToDismiss = _a.useEscKeyToDismiss;
            if (dismissible &&
                useEscKeyToDismiss &&
                event.keyCode === 27 /* ESCAPE */) {
                event.stopPropagation();
                event.preventDefault();
                _this.dismiss();
            }
        };
        _this.contentClickHandler = function (event) {
            // A click in the content should not propagate to the backdrop element,
            // because the backdrop listens to clicks to close the modal.
            event.stopPropagation();
        };
        _this.dismiss = function () {
            if (typeof _this.props.onDismiss === 'function') {
                _this.props.onDismiss();
            }
        };
        _this.renderContent = function () {
            var _a = _this.props, dismissible = _a.dismissible, children = _a.children, fullviewport = _a.fullviewport, header = _a.header;
            var iconDescription = 'Schließen';
            return (React.createElement(React.Fragment, null,
                (dismissible || (fullviewport && header)) && (React.createElement("div", { className: classNames('ee_modal__header', {
                        'ee_modal__header--with-content': fullviewport && header
                    }) },
                    fullviewport && header,
                    dismissible && !(fullviewport && header) && (React.createElement("button", { type: "button", className: "ee_modal__close-button", onClick: _this.dismiss, "aria-label": iconDescription },
                        React.createElement(CloseIcon_1.default, { title: iconDescription, className: "ee_modal__close-icon", width: 22, height: 22 }))))),
                React.createElement("section", { className: "ee_modal__content" }, children)));
        };
        _this.renderModal = function (componentClassName, fullviewport, id) { return (React.createElement("div", { ref: function (element) { return (_this.backdropElement = element); }, className: componentClassName, onClick: _this.handleBackdrop, onKeyDown: _this.handleKeyDown, tabIndex: 0, id: id },
            !fullviewport && (React.createElement("div", { className: "ee_modal__main", onClick: _this.contentClickHandler }, _this.renderContent())),
            fullviewport && _this.renderContent())); };
        _this.createPortalContainer();
        return _this;
    }
    UnmanagedModalWithinContext.prototype.clearDismissTimeout = function () {
        if (this.timeoutHandler) {
            // Use reference window to prevent error in node environments (when running the build)
            // See: https://github.com/Microsoft/TypeScript/issues/842
            window.clearTimeout(this.timeoutHandler);
            this.timeoutHandler = null;
        }
    };
    UnmanagedModalWithinContext.prototype.setDismissTimeout = function () {
        var _this = this;
        var timeout = typeof this.props.dismissTimeout === 'number'
            ? this.props.dismissTimeout
            : 0;
        // Use reference window to prevent error in node environments (when running the build)
        // See: https://github.com/Microsoft/TypeScript/issues/842
        this.timeoutHandler = window.setTimeout(function () {
            _this.dismiss();
            _this.clearDismissTimeout();
        }, timeout);
    };
    UnmanagedModalWithinContext.prototype.componentDidMount = function () {
        var _a = this.props, dismissTimeout = _a.dismissTimeout, open = _a.open;
        document.body.appendChild(this.portalContainer);
        if (open && dismissTimeout > 0) {
            this.setDismissTimeout();
        }
    };
    UnmanagedModalWithinContext.prototype.componentDidUpdate = function (prevProps) {
        var theme = this.props.ergoElements.theme;
        this.portalContainer.setAttribute('data-theme', theme);
        // In case the dialog has been opened, give it the focus.
        if (!prevProps.open && this.props.open) {
            this.backdropElement.focus();
        }
        if (this.props.open !== prevProps.open) {
            this.clearDismissTimeout();
            if (this.props.dismissTimeout > 0 && this.props.open) {
                this.setDismissTimeout();
            }
        }
    };
    UnmanagedModalWithinContext.prototype.componentWillUnmount = function () {
        this.clearDismissTimeout();
        document.body.removeChild(this.portalContainer);
    };
    UnmanagedModalWithinContext.prototype.render = function () {
        var _a = this.props, className = _a.className, fullviewport = _a.fullviewport, id = _a.id, open = _a.open, viewport = _a.viewport;
        var componentClassName = classNames(className, 'ee_modal', {
            'ee_modal--relative': viewport === 'relative',
            'ee_modal--hidden': !open,
            'ee_modal--fullviewport': fullviewport
        });
        return viewport === 'relative'
            ? this.renderModal(componentClassName, fullviewport, id)
            : ReactDOM.createPortal(this.renderModal(componentClassName, fullviewport, id), this.portalContainer);
    };
    UnmanagedModalWithinContext.propTypes = propTypes;
    UnmanagedModalWithinContext.defaultProps = {
        backdropDismissesModal: false,
        fullviewport: false,
        header: undefined,
        dismissible: false,
        dismissTimeout: undefined,
        onDismiss: undefined,
        open: undefined,
        useEscKeyToDismiss: true,
        viewport: 'fixed'
    };
    return UnmanagedModalWithinContext;
}(react_1.Component));
exports.UnmanagedModalWithinContext = UnmanagedModalWithinContext;
exports.default = UnmanagedModalWithinContext;
//# sourceMappingURL=UnmanagedModalWithinContext.js.map
